<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import AiAgentsAPI from 'dashboard/api/aiAgents';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  node: { type: Object, default: null },
  flowSettings: { type: Object, required: true },
});

const emit = defineEmits([
  'updateNodeConfig',
  'updateFlowSettings',
  'deleteNode',
]);

const { t } = useI18n();
const store = useStore();
const agents = useMapGetter('agents/getVerifiedAgents');
const labels = useMapGetter('labels/getLabels');
const aiAgents = ref([]);

const kind = computed(() => props.node?.data?.kind);
const config = computed(() => props.node?.data?.config || {});

const agentOptions = computed(() =>
  agents.value.map(agent => ({
    value: agent.id,
    label: agent.name || agent.email,
  }))
);

const labelOptions = computed(() =>
  labels.value.map(label => ({ value: label.title, label: label.title }))
);

const aiAgentOptions = computed(() =>
  aiAgents.value.map(agent => ({ value: agent.id, label: agent.name }))
);

const triggerOptions = computed(() => [
  { value: 'no_reply', label: t('FLOWS.SETTINGS.TRIGGER_NO_REPLY') },
  { value: 'stage_entry', label: t('FLOWS.SETTINGS.TRIGGER_STAGE_ENTRY') },
  { value: 'manual', label: t('FLOWS.SETTINGS.TRIGGER_MANUAL') },
]);

const conditionAttributes = computed(() => [
  {
    value: 'pipeline_stage',
    label: t('FLOWS.CONFIG.ATTRIBUTE_PIPELINE_STAGE'),
  },
  {
    value: 'contact_replied',
    label: t('FLOWS.CONFIG.ATTRIBUTE_CONTACT_REPLIED'),
  },
]);

const conditionOperators = computed(() => [
  { value: 'equals', label: t('FLOWS.CONFIG.OPERATOR_EQUALS') },
  { value: 'not_equals', label: t('FLOWS.CONFIG.OPERATOR_NOT_EQUALS') },
  { value: 'contains', label: t('FLOWS.CONFIG.OPERATOR_CONTAINS') },
  { value: 'present', label: t('FLOWS.CONFIG.OPERATOR_PRESENT') },
  { value: 'blank', label: t('FLOWS.CONFIG.OPERATOR_BLANK') },
]);

const agentModeOptions = computed(() => [
  {
    value: 'last_responding',
    label: t('FLOWS.CONFIG.AGENT_MODE_LAST_RESPONDING'),
  },
  { value: 'specific', label: t('FLOWS.CONFIG.AGENT_MODE_SPECIFIC') },
  { value: 'unassign', label: t('FLOWS.CONFIG.AGENT_MODE_UNASSIGN') },
]);

const statusOptions = computed(() => [
  { value: 'open', label: t('FLOWS.CONFIG.STATUS_OPEN') },
  { value: 'pending', label: t('FLOWS.CONFIG.STATUS_PENDING') },
  { value: 'resolved', label: t('FLOWS.CONFIG.STATUS_RESOLVED') },
]);

const labelOperationOptions = computed(() => [
  { value: 'add', label: t('FLOWS.CONFIG.LABEL_ADD') },
  { value: 'remove', label: t('FLOWS.CONFIG.LABEL_REMOVE') },
]);

const httpMethodOptions = computed(() => [
  { value: 'get', label: t('FLOWS.CONFIG.HTTP_METHOD_GET') },
  { value: 'post', label: t('FLOWS.CONFIG.HTTP_METHOD_POST') },
  { value: 'put', label: t('FLOWS.CONFIG.HTTP_METHOD_PUT') },
  { value: 'patch', label: t('FLOWS.CONFIG.HTTP_METHOD_PATCH') },
  { value: 'delete', label: t('FLOWS.CONFIG.HTTP_METHOD_DELETE') },
]);

const nodeTitle = computed(() => props.node?.data?.label || '');

const updateConfig = (key, value) => {
  emit('updateNodeConfig', {
    ...props.node,
    data: {
      ...props.node.data,
      config: { ...config.value, [key]: value },
    },
  });
};

const updateFlowSetting = (key, value) => {
  emit('updateFlowSettings', { ...props.flowSettings, [key]: value });
};

const updateCase = (index, key, value) => {
  const cases = [...(config.value.cases || [])];
  cases[index] = { ...cases[index], [key]: value };
  updateConfig('cases', cases);
};

const addCase = () => {
  const cases = config.value.cases || [];
  const usedIndexes = cases.map(flowCase => {
    const match = flowCase.branch?.match(/^case_(\d+)$/);
    return match ? Number(match[1]) : 0;
  });
  const nextIndex = Math.max(0, ...usedIndexes) + 1;
  updateConfig('cases', [
    ...cases,
    {
      branch: `case_${nextIndex}`,
      attribute: 'pipeline_stage',
      operator: 'equals',
      value: '',
    },
  ]);
};

const removeCase = index => {
  updateConfig(
    'cases',
    (config.value.cases || []).filter((_, caseIndex) => caseIndex !== index)
  );
};

onMounted(async () => {
  store.dispatch('agents/get');
  store.dispatch('labels/get');
  try {
    const { data } = await AiAgentsAPI.getAgents();
    aiAgents.value = Array.isArray(data) ? data : [];
  } catch {
    aiAgents.value = [];
  }
});
</script>

<template>
  <aside
    class="flex flex-col flex-none w-80 min-h-0 border-s border-n-weak bg-n-solid-1"
  >
    <div class="flex items-start gap-3 px-4 py-4 border-b border-n-weak">
      <div class="flex-1 min-w-0">
        <p
          class="text-xs font-semibold uppercase tracking-[0.14em] text-n-slate-10"
        >
          {{ t('FLOWS.CONFIG.TITLE') }}
        </p>
        <h2 class="mt-1 text-sm font-semibold truncate text-n-slate-12">
          {{ node ? nodeTitle : t('FLOWS.CONFIG.EMPTY_TITLE') }}
        </h2>
      </div>
      <Button
        v-if="node && kind !== 'start'"
        v-tooltip="t('FLOWS.CONFIG.DELETE_NODE')"
        size="xs"
        color="ruby"
        icon="i-lucide-trash-2"
        @click="emit('deleteNode', node.id)"
      />
    </div>

    <div v-if="!node" class="flex flex-col gap-3 p-5 text-sm text-n-slate-11">
      <span class="i-lucide-mouse-pointer-click text-3xl text-n-slate-8" />
      <p>{{ t('FLOWS.CONFIG.EMPTY_DESCRIPTION') }}</p>
    </div>

    <div v-else class="flex-1 min-h-0 p-4 overflow-y-auto">
      <div v-if="kind === 'start'" class="flex flex-col gap-4">
        <p class="text-xs leading-5 text-n-slate-11">
          {{ t('FLOWS.CONFIG.START_DESCRIPTION') }}
        </p>
        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          {{ t('FLOWS.SETTINGS.TRIGGER') }}
          <Select
            :model-value="flowSettings.trigger_type"
            :options="triggerOptions"
            @update:model-value="
              value => updateFlowSetting('trigger_type', value)
            "
          />
        </label>
        <Input
          :model-value="String(flowSettings.trigger_minutes || 60)"
          type="number"
          :label="t('FLOWS.SETTINGS.DELAY_MINUTES')"
          @update:model-value="
            value => updateFlowSetting('trigger_minutes', value)
          "
        />
        <Input
          v-if="flowSettings.trigger_type === 'stage_entry'"
          :model-value="flowSettings.trigger_stage || ''"
          :label="t('FLOWS.SETTINGS.STAGE')"
          @update:model-value="
            value => updateFlowSetting('trigger_stage', value)
          "
        />
        <Input
          :model-value="
            flowSettings.daily_message_cap === null
              ? ''
              : String(flowSettings.daily_message_cap)
          "
          type="number"
          :label="t('FLOWS.SETTINGS.DAILY_CAP')"
          @update:model-value="
            value => updateFlowSetting('daily_message_cap', value)
          "
        />
        <p class="text-xs leading-5 text-n-slate-10">
          {{ t('FLOWS.SETTINGS.DAILY_CAP_HELP') }}
        </p>
        <div class="flex flex-col gap-2">
          <p class="text-sm text-n-slate-12">
            {{ t('FLOWS.SETTINGS.SEND_WINDOW') }}
          </p>
          <div class="flex items-end gap-2">
            <Input
              :model-value="
                flowSettings.send_window_start_hour === null
                  ? ''
                  : String(flowSettings.send_window_start_hour)
              "
              type="number"
              :label="t('FLOWS.SETTINGS.SEND_WINDOW_START')"
              @update:model-value="
                value => updateFlowSetting('send_window_start_hour', value)
              "
            />
            <Input
              :model-value="
                flowSettings.send_window_end_hour === null
                  ? ''
                  : String(flowSettings.send_window_end_hour)
              "
              type="number"
              :label="t('FLOWS.SETTINGS.SEND_WINDOW_END')"
              @update:model-value="
                value => updateFlowSetting('send_window_end_hour', value)
              "
            />
          </div>
          <p class="text-xs leading-5 text-n-slate-10">
            {{ t('FLOWS.SETTINGS.SEND_WINDOW_HELP') }}
          </p>
        </div>
      </div>

      <div v-else-if="kind === 'send_text'" class="flex flex-col gap-4">
        <TextArea
          :model-value="config.text || ''"
          :label="t('FLOWS.NODES.SEND_TEXT.TITLE')"
          :placeholder="t('FLOWS.NODES.SEND_TEXT.PLACEHOLDER')"
          custom-text-area-class="min-h-40"
          resize
          @update:model-value="value => updateConfig('text', value)"
        />
        <p class="text-xs leading-5 text-n-slate-10">
          {{ t('FLOWS.NODES.SEND_TEXT.HELP') }}
        </p>
        <Input
          :model-value="config.template_name || ''"
          :label="t('FLOWS.CONFIG.TEMPLATE_NAME')"
          :placeholder="t('FLOWS.CONFIG.TEMPLATE_NAME_PLACEHOLDER')"
          @update:model-value="value => updateConfig('template_name', value)"
        />
        <Input
          :model-value="config.template_language || ''"
          :label="t('FLOWS.CONFIG.TEMPLATE_LANGUAGE')"
          :placeholder="t('FLOWS.CONFIG.TEMPLATE_LANGUAGE_PLACEHOLDER')"
          @update:model-value="
            value => updateConfig('template_language', value)
          "
        />
        <Input
          :model-value="config.template_variables || ''"
          :label="t('FLOWS.CONFIG.TEMPLATE_VARIABLES')"
          :placeholder="t('FLOWS.CONFIG.TEMPLATE_VARIABLES_PLACEHOLDER')"
          @update:model-value="
            value => updateConfig('template_variables', value)
          "
        />
        <p class="text-xs leading-5 text-n-slate-10">
          {{ t('FLOWS.CONFIG.TEMPLATE_HELP') }}
        </p>
      </div>

      <div v-else-if="kind === 'wait'" class="flex flex-col gap-4">
        <Input
          :model-value="String(config.minutes || 60)"
          type="number"
          :label="t('FLOWS.CONFIG.WAIT_MINUTES')"
          @update:model-value="value => updateConfig('minutes', Number(value))"
        />
        <p class="text-xs leading-5 text-n-slate-10">
          {{ t('FLOWS.NODES.WAIT.HELP') }}
        </p>
      </div>

      <div v-else-if="kind === 'condition'" class="flex flex-col gap-4">
        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          {{ t('FLOWS.CONFIG.ATTRIBUTE') }}
          <Select
            :model-value="config.attribute || 'pipeline_stage'"
            :options="conditionAttributes"
            @update:model-value="value => updateConfig('attribute', value)"
          />
        </label>
        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          {{ t('FLOWS.CONFIG.OPERATOR') }}
          <Select
            :model-value="config.operator || 'equals'"
            :options="conditionOperators"
            @update:model-value="value => updateConfig('operator', value)"
          />
        </label>
        <Input
          v-if="!['present', 'blank'].includes(config.operator)"
          :model-value="config.value || ''"
          :label="t('FLOWS.CONFIG.VALUE')"
          @update:model-value="value => updateConfig('value', value)"
        />
        <p class="text-xs leading-5 text-n-slate-10">
          {{ t('FLOWS.NODES.CONDITION.HELP') }}
        </p>
      </div>

      <div v-else-if="kind === 'switch'" class="flex flex-col gap-4">
        <div
          v-for="(flowCase, index) in config.cases || []"
          :key="flowCase.branch"
          class="flex flex-col gap-3 p-3 border rounded-xl border-n-weak bg-n-background"
        >
          <div class="flex items-center gap-2">
            <Input
              :model-value="flowCase.branch"
              :label="t('FLOWS.CONFIG.CASE_KEY')"
              disabled
              @update:model-value="value => updateCase(index, 'branch', value)"
            />
            <Button
              size="xs"
              color="ruby"
              icon="i-lucide-trash-2"
              class="mt-5"
              @click="removeCase(index)"
            />
          </div>
          <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
            {{ t('FLOWS.CONFIG.ATTRIBUTE') }}
            <Select
              :model-value="flowCase.attribute || 'pipeline_stage'"
              :options="conditionAttributes"
              @update:model-value="
                value => updateCase(index, 'attribute', value)
              "
            />
          </label>
          <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
            {{ t('FLOWS.CONFIG.OPERATOR') }}
            <Select
              :model-value="flowCase.operator || 'equals'"
              :options="conditionOperators"
              @update:model-value="
                value => updateCase(index, 'operator', value)
              "
            />
          </label>
          <Input
            v-if="!['present', 'blank'].includes(flowCase.operator)"
            :model-value="flowCase.value || ''"
            :label="t('FLOWS.CONFIG.VALUE')"
            @update:model-value="value => updateCase(index, 'value', value)"
          />
        </div>
        <Button
          size="sm"
          color="slate"
          icon="i-lucide-plus"
          :disabled="(config.cases || []).length >= 3"
          @click="addCase"
        >
          {{ t('FLOWS.CONFIG.ADD_CASE') }}
        </Button>
      </div>

      <div v-else-if="kind === 'ai_agent'" class="flex flex-col gap-4">
        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          {{ t('FLOWS.CONFIG.AI_AGENT') }}
          <ComboBox
            :model-value="config.agent_id || ''"
            :options="aiAgentOptions"
            :placeholder="t('FLOWS.CONFIG.AI_AGENT_PLACEHOLDER')"
            @update:model-value="
              value => updateConfig('agent_id', value ? Number(value) : null)
            "
          />
        </label>
        <p class="text-xs leading-5 text-n-slate-10">
          {{ t('FLOWS.CONFIG.AI_AGENT_HELP') }}
        </p>
      </div>

      <div v-else-if="kind === 'capture_input'" class="flex flex-col gap-4">
        <Input
          :model-value="config.variable || ''"
          :label="t('FLOWS.CONFIG.CAPTURE_VARIABLE')"
          :placeholder="t('FLOWS.CONFIG.CAPTURE_VARIABLE_PLACEHOLDER')"
          @update:model-value="value => updateConfig('variable', value)"
        />
        <p class="text-xs leading-5 text-n-slate-10">
          {{ t('FLOWS.CONFIG.CAPTURE_HELP') }}
        </p>
      </div>

      <div v-else-if="kind === 'assign_agent'" class="flex flex-col gap-4">
        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          {{ t('FLOWS.CONFIG.AGENT_MODE') }}
          <Select
            :model-value="config.mode || 'last_responding'"
            :options="agentModeOptions"
            @update:model-value="value => updateConfig('mode', value)"
          />
        </label>
        <label
          v-if="config.mode === 'specific'"
          class="flex flex-col gap-1.5 text-sm text-n-slate-12"
        >
          {{ t('FLOWS.CONFIG.AGENT') }}
          <ComboBox
            :model-value="config.agent_id || ''"
            :options="agentOptions"
            :placeholder="t('FLOWS.CONFIG.AGENT_PLACEHOLDER')"
            @update:model-value="
              value => updateConfig('agent_id', value ? Number(value) : null)
            "
          />
        </label>
        <p class="text-xs leading-5 text-n-slate-10">
          {{ t('FLOWS.CONFIG.AGENT_HELP') }}
        </p>
      </div>

      <div v-else-if="kind === 'http_request'" class="flex flex-col gap-4">
        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          {{ t('FLOWS.CONFIG.HTTP_METHOD') }}
          <Select
            :model-value="config.method || 'post'"
            :options="httpMethodOptions"
            @update:model-value="value => updateConfig('method', value)"
          />
        </label>
        <Input
          :model-value="config.url || ''"
          :label="t('FLOWS.CONFIG.HTTP_URL')"
          :placeholder="t('FLOWS.CONFIG.HTTP_URL_PLACEHOLDER')"
          @update:model-value="value => updateConfig('url', value)"
        />
        <TextArea
          :model-value="config.headers || '{}'"
          :label="t('FLOWS.CONFIG.HTTP_HEADERS')"
          :placeholder="t('FLOWS.CONFIG.HTTP_HEADERS_PLACEHOLDER')"
          custom-text-area-class="min-h-24 font-mono"
          @update:model-value="value => updateConfig('headers', value)"
        />
        <TextArea
          :model-value="config.body || ''"
          :label="t('FLOWS.CONFIG.HTTP_BODY')"
          :placeholder="t('FLOWS.CONFIG.HTTP_BODY_PLACEHOLDER')"
          custom-text-area-class="min-h-32 font-mono"
          @update:model-value="value => updateConfig('body', value)"
        />
        <Input
          :model-value="config.response_variable || ''"
          :label="t('FLOWS.CONFIG.HTTP_RESPONSE_VARIABLE')"
          :placeholder="t('FLOWS.CONFIG.HTTP_RESPONSE_VARIABLE_PLACEHOLDER')"
          @update:model-value="
            value => updateConfig('response_variable', value)
          "
        />
        <Input
          :model-value="String(config.timeout_seconds || 10)"
          type="number"
          :label="t('FLOWS.CONFIG.HTTP_TIMEOUT')"
          @update:model-value="
            value => updateConfig('timeout_seconds', Number(value))
          "
        />
        <p class="text-xs leading-5 text-n-slate-10">
          {{ t('FLOWS.CONFIG.HTTP_HELP') }}
        </p>
      </div>

      <div v-else-if="kind === 'set_status'" class="flex flex-col gap-4">
        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          {{ t('FLOWS.CONFIG.STATUS') }}
          <Select
            :model-value="config.status || 'open'"
            :options="statusOptions"
            @update:model-value="value => updateConfig('status', value)"
          />
        </label>
        <p class="text-xs leading-5 text-n-slate-10">
          {{ t('FLOWS.CONFIG.STATUS_HELP') }}
        </p>
      </div>

      <div v-else-if="kind === 'set_labels'" class="flex flex-col gap-4">
        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          {{ t('FLOWS.CONFIG.LABEL_OPERATION') }}
          <Select
            :model-value="config.operation || 'add'"
            :options="labelOperationOptions"
            @update:model-value="value => updateConfig('operation', value)"
          />
        </label>
        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          {{ t('FLOWS.CONFIG.LABELS') }}
          <TagMultiSelectComboBox
            :model-value="config.labels || []"
            :options="labelOptions"
            :placeholder="t('FLOWS.CONFIG.LABELS_PLACEHOLDER')"
            @update:model-value="value => updateConfig('labels', value)"
          />
        </label>
        <p class="text-xs leading-5 text-n-slate-10">
          {{ t('FLOWS.CONFIG.LABELS_HELP') }}
        </p>
      </div>

      <div v-else-if="kind === 'end'" class="flex flex-col gap-4">
        <Input
          :model-value="config.reason || ''"
          :label="t('FLOWS.CONFIG.END_REASON')"
          @update:model-value="value => updateConfig('reason', value)"
        />
      </div>
    </div>
  </aside>
</template>
